import React from 'react';
import PropTypes from 'prop-types';
import { LuCircleAlert, LuCircleCheck } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import GlassIcon from '../../../components/ui/GlassIcon';
import { useTranslation } from '../../../i18n/LanguageContext';
import { STEP_META, useStepStatusText } from './VisitStepper';
import { focusField } from './VisitUi';

const BADGE_TONES = { missing: 'warning', done: 'success', info: 'neutral', optional: 'neutral' };

/**
 * Top of every step: "Adım 3 / 7", the title, what to do on this page and -
 * when required fields are empty - chips that jump straight to each of them.
 */
const StepHeader = ({ step, number, total, help, autosave }) => {
  const { t } = useTranslation();
  const statusText = useStepStatusText();
  const meta = STEP_META[step.key];
  const Icon = meta.icon;

  return (
    <div className="tw-space-y-3">
      <div className="tw-flex tw-items-start tw-gap-3.5">
        <GlassIcon gradient={meta.gradient} size="md" className="max-sm:tw-hidden">
          <Icon aria-hidden="true" className="tw-h-6 tw-w-6" />
        </GlassIcon>
        <div className="tw-min-w-0 tw-flex-1">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">
              {t('visitModal.progress.stepOf', { current: number, total })}
            </span>
            <Badge tone={step.state === 'optional' && step.records > 0 ? 'info' : BADGE_TONES[step.state]}>{statusText(step)}</Badge>
            {autosave && (
              <Badge tone="accent">
                <LuCircleCheck aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                {t('visitModal.progress.autosave')}
              </Badge>
            )}
          </div>
          <h3 className="tw-mt-1 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">
            {t(`visitModal.steps.${step.key}.title`)}
          </h3>
          <div className="tw-mt-1 tw-max-w-3xl tw-space-y-1 tw-text-sm tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">{help}</div>
        </div>
      </div>

      {step.missing.length > 0 && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-rounded-2xl tw-border tw-border-amber-500/25 tw-bg-amber-400/[0.08] tw-px-3 tw-py-2.5">
          <span className="tw-mr-1 tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-amber-800 dark:tw-text-amber-200">
            <LuCircleAlert aria-hidden="true" className="tw-h-4 tw-w-4" />
            {t('visitModal.status.missingFields')}
          </span>
          {step.missing.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => focusField(item.key)}
              className="tw-rounded-full tw-border tw-border-amber-500/30 tw-bg-white/80 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-amber-900 tw-transition-colors hover:tw-bg-white focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-amber-500/50 dark:tw-bg-white/[0.06] dark:tw-text-amber-100 dark:hover:tw-bg-white/[0.1]"
            >
              {t(`visitModal.fields.${item.key}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

StepHeader.propTypes = {
  step: PropTypes.shape({
    key: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    missing: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired })).isRequired,
    records: PropTypes.number,
  }).isRequired,
  number: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  help: PropTypes.node.isRequired,
  autosave: PropTypes.bool,
};

export default StepHeader;
